import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import AppShell from "../../../components/layout/AppShell";
import StatCard from "../../../components/ui/StatCard";
import InvoiceHistoryList from "./InvoiceHistoryList";
import { getSenderInvoices } from "../../../services/api";
import { formatAmount, getMaxDecimalsForCurrency, getDateFilterRange } from "../../../utils";
import { DATE_FILTER_OPTIONS } from "../../../utils/constants";
import AnalyticsSummaryCards from "../../../components/ui/AnalyticsSummaryCards";
import { getPaymentRequestAnalytics } from "../../../services/analytics.api";
import useAnalytics from "../../../hooks/useAnalytics";
import { INVOICE_ANALYTICS_CONFIG } from "../../../config/transactionAnalytics.config";
const ANALYTICS_ENABLED = true;

// Kill-switch for this page's invoice-history <DateFilter />: hides the UI and
// stops sending from/to params to the API.
// TODO: remove once the backend supports date-range filtering on sender invoices.
const DISABLE_DATE_FILTER = false;

// Kill-switch for this page's analytics <DateFilter />.
const DISABLE_DATE_FILTER_FOR_ANALYTICS = false;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const STATUS_MAP = {
  "Paid":        "success",
  "In Progress": "processing",
  "Pending":     "created",
};

const fmtCcy = (amount, currency = "USD") => {
  const n = Number(amount);
  if (isNaN(n)) return "—";
  const ccy = (currency || "USD").toUpperCase();
  return `${ccy} ${formatAmount(amount, getMaxDecimalsForCurrency(ccy))}`;
};

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// ─── Component ────────────────────────────────────────────────────────────────
const Invoices = () => {
  const userId = useSelector((s) => s.auth.userId);

  // Analytics summary (feature-flagged)
  const [analyticsDateRange, setAnalyticsDateRange] = useState(() =>
    getDateFilterRange(DATE_FILTER_OPTIONS.MONTH_TILL_DATE)
  );
  const {
    value: invoiceAnalyticsItems,
    loading: invoiceAnalyticsLoading,
  } = useAnalytics(
    getPaymentRequestAnalytics,
    DISABLE_DATE_FILTER_FOR_ANALYTICS
      ? {}
      : { from: analyticsDateRange.startDate, to: analyticsDateRange.endDate },
    { enabled: ANALYTICS_ENABLED }
  );

  // ── History (server-paginated, 20 per page) ──────────────────────────────
  const [history, setHistory]             = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyPage, setHistoryPage]     = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10);
  const [historyTotal, setHistoryTotal]   = useState(0);
  const [historyStatus, setHistoryStatus] = useState("All");
  const [historySearch, setHistorySearch] = useState("");
  const debouncedHistorySearch = useDebounce(historySearch, 400);
  const [historyDateRange, setHistoryDateRange] = useState(() =>
    getDateFilterRange(DATE_FILTER_OPTIONS.MONTH_TILL_DATE)
  );

  // Reset to page 1 whenever the search query settles
  useEffect(() => {
    setHistoryPage(1);
  }, [debouncedHistorySearch]);

  // ── Load history invoices ────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    setHistoryLoading(true);
    const params = { page: historyPage, limit: historyPageSize };
    if (historyStatus !== "All") params.status = STATUS_MAP[historyStatus];
    if (debouncedHistorySearch) params.searchQuery = debouncedHistorySearch;
    if (!DISABLE_DATE_FILTER) {
      if (historyDateRange.startDate) params.from = historyDateRange.startDate;
      if (historyDateRange.endDate) params.to = historyDateRange.endDate;
    }
    getSenderInvoices(userId, params)
      .then((res) => {
        const data = res?.data || [];
        setHistory(data);
        setHistoryTotal(res?.meta?.total || data.length);
      })
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [userId, historyPage, historyPageSize, historyStatus, debouncedHistorySearch, historyDateRange]);

  const changeHistoryStatus = (t) => {
    setHistoryStatus(t);
    setHistoryPage(1);
  };

  const changeHistoryPageSize = (newSize) => {
    setHistoryPageSize(newSize);
    setHistoryPage(1);
  };

  const changeHistoryDateFilter = (_filter, range) => {
    setHistoryDateRange((prev) =>
      prev.startDate === range.startDate && prev.endDate === range.endDate ? prev : range
    );
    setHistoryPage(1);
  };

  const analyticsItems = (invoiceAnalyticsItems != null) ? INVOICE_ANALYTICS_CONFIG.map(({ key, title ,valueClassName }) => {
    if(key === "overDueCount") {
      if (!invoiceAnalyticsItems?.[key]) return null;
      return {
        title,
        value:invoiceAnalyticsItems?.[key] || 0,
        valueClassName,
      }
    }
    
    const data = invoiceAnalyticsItems?.[key] || [];
    const count = invoiceAnalyticsItems?.[key]?.count;
    if (!count) return null;
    return {
      title,
      prefix: data?.currency?.toUpperCase(),
      prefixClassName : valueClassName,
      value: formatAmount(data?.amount, getMaxDecimalsForCurrency(data?.currency)),
      subLabel: `${count ?? 0} Transactions`,
      valueClassName,
    };
  }).filter(Boolean) : [];

  return (
    <AppShell>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          View and manage all invoices
        </p>
      </div>

      {/* Analytics summary (feature-flagged) */}
      {analyticsItems.length > 0 && (
        <AnalyticsSummaryCards
          items={analyticsItems}
          loading={invoiceAnalyticsLoading}
          showDateFilter={!DISABLE_DATE_FILTER_FOR_ANALYTICS}
          onDateFilterChange={(_filter, range) =>
            setAnalyticsDateRange((prev) =>
              prev.startDate === range.startDate && prev.endDate === range.endDate ? prev : range
            )
          }
        />
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard
          label="Total Paid"
          value={
            invoiceAnalyticsLoading
              ? "—"
              : fmtCcy(invoiceAnalyticsItems?.paid?.amount, invoiceAnalyticsItems?.paid?.currency)
          }
          subLabel="This month"
        />
        <StatCard
          label="Outstanding"
          value={
            invoiceAnalyticsLoading
              ? "—"
              : fmtCcy(invoiceAnalyticsItems?.outStanding?.amount, invoiceAnalyticsItems?.outStanding?.currency)
          }
          subLabel="On invoice"
        />
        <StatCard
          label="Overdue"
          value={invoiceAnalyticsLoading ? "—" : (invoiceAnalyticsItems?.overDueCount ?? 0).toString()}
          valueColor="text-yellow-500"
          subLabel="Past due date"
        />
      </div>

      {/* ── Invoice history ─────────────────────────────────────────────────── */}
      <InvoiceHistoryList
        data={history}
        loading={historyLoading}
        total={historyTotal}
        page={historyPage}
        pageSize={historyPageSize}
        status={historyStatus}
        onStatusChange={changeHistoryStatus}
        search={historySearch}
        onSearchChange={setHistorySearch}
        onPageChange={setHistoryPage}
        onPageSizeChange={changeHistoryPageSize}
        disableDateFilter={DISABLE_DATE_FILTER}
        onDateFilterChange={changeHistoryDateFilter}
      />
    </AppShell>
  );
};

export default Invoices;
